// src/components/enduser/subscription/SubscriptionPage.tsx - תיקון משתנה לא בשימוש

import React, { useState, useEffect } from "react";
import { useEnduserAuth } from "../../../contexts/EnduserAuthContext";
import subscriptionService from "../../../services/enduser/subscriptionService";
import { useNavigate } from "react-router-dom";
import FreeTrialOfferPopup from "./FreeTrialOfferPopup";

// Grow Wallet SDK TypeScript declarations
declare global {
  interface Window {
    growPayment?: {
      init: (config: GrowPaymentConfig) => void;
      updateEvents?: (events: GrowPaymentConfig["events"]) => void;
      renderPaymentOptions: (authCode: string) => void;
    };
    // ה-runtime האמיתי (mp.min.js) — קיים רק אחרי שה-bundle סיים להיטען
    growRuntime?: unknown;
  }
}

interface GrowPaymentConfig {
  environment: "DEV" | "PRODUCTION";
  version: number;
  events: {
    onSuccess?: (response: GrowSuccessResponse) => void;
    onFailure?: (response: GrowFailureResponse) => void;
    onError?: (response: GrowErrorResponse) => void;
    onTimeout?: (response: any) => void;
    onWalletChange?: (state: "open" | "close") => void;
    onPaymentStart?: (response: GrowPaymentStartResponse) => void;
    onPaymentCancel?: (response: GrowPaymentCancelResponse) => void;
  };
}

interface GrowSuccessResponse {
  status: 1;
  data: {
    payment_sum: string;
    full_name: string;
    payment_method: "credit" | "bank_transfer" | "bit" | "google" | "apple";
    number_of_payments: number;
    confirmation_number: string;
  };
}

interface GrowFailureResponse {
  status: 0;
  message: string;
}

interface GrowErrorResponse {
  status: 0;
  message: string;
}

interface GrowPaymentStartResponse {
  paymentType: number;
  paymentName: string;
}

interface GrowPaymentCancelResponse {
  paymentType: number;
  paymentName: string;
}

interface SubscriptionPlan {
  id: number;
  plan_name: string;
  name_hebrew: string;
  duration_months: number;
  monthly_price: number;
  total_price: number;
  description: string;
  features: string[];
  popular: boolean;
  discount_percentage: number;
}

interface SubscriptionPageProps {}

const SubscriptionPage: React.FC<SubscriptionPageProps> = () => {
  const navigate = useNavigate();
  const { verifySession, subscriptionStatus } = useEnduserAuth();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [saveCardConsent, setSaveCardConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [isEligibleForGrace, setIsEligibleForGrace] = useState<boolean>(true);

  // ===== קופון גישה חופשית =====
  const [showCouponPopup, setShowCouponPopup] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponSubmitting, setCouponSubmitting] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);

  // טעינת תוכניות המנוי
  useEffect(() => {
    fetchSubscriptionPlans();
    // רענון סטטוס המנוי כדי שחישוב הזכאות לפופאפ "ניסיון חינם" יתבסס על נתונים עדכניים
    // (חשוב במיוחד כשמשתמש חדש מועבר לכאן מיד אחרי התחברות).
    verifySession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // חיבור ה-callbacks של הדף ל-Grow Wallet SDK.
  // ה-init עצמו נעשה פעם אחת ב-public/index.html — init נוסף כאן היה
  // יוצר runtime שני ושובר את טופס התשלום. כאן רק ממתינים ל-runtime
  // (growRuntime, לא growPayment!) ומעדכנים events.
  useEffect(() => {
    let cancelled = false;
    let pollId: number | undefined;

    const waitForGrowRuntime = () =>
      new Promise<boolean>((resolve) => {
        if (window.growRuntime && window.growPayment) return resolve(true);
        const startedAt = Date.now();
        pollId = window.setInterval(() => {
          const ready = !!(window.growRuntime && window.growPayment);
          // 15 שניות: ההמתנה כוללת gs.min.js + api-versions.json + mp.min.js
          if (ready || Date.now() - startedAt > 15000) {
            window.clearInterval(pollId);
            resolve(ready);
          }
        }, 100);
      });

    waitForGrowRuntime().then((ready) => {
      if (cancelled) return;
      if (!ready || !window.growPayment?.updateEvents) {
        console.error("❌ Grow SDK failed to load");
        setError("שגיאה בטעינת מערכת התשלום");
        return;
      }

      window.growPayment.updateEvents({
        onSuccess: (response) => {
          handlePaymentSuccess(response);
        },
        onFailure: (response) => {
          setError(response.message || "התשלום נכשל");
          setProcessing(false);
        },
        onError: (response) => {
          setError(response.message || "שגיאה בתשלום");
          setProcessing(false);
        },
        onTimeout: () => {
          setError("פג תוקף התשלום, נסה שנית");
          setProcessing(false);
        },
        onWalletChange: (state) => {
          if (state === "close") {
            setProcessing(false);
          }
        },
        onPaymentStart: () => {},
        onPaymentCancel: () => {
          setProcessing(false);
        },
      });

      setSdkReady(true);
    });

    return () => {
      cancelled = true;
      if (pollId) window.clearInterval(pollId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // useEffect(() => {
  //   const checkEligibility = async () => {
  //     try {
  //       const result = await checkGraceEligibility();
  //       setIsEligibleForGrace(result.eligible_for_grace_period);
  //     } catch (error) {
  //       console.error('Error checking grace eligibility:', error);
  //     }
  //   };
  //   checkEligibility();
  // }, []);

  // טיפול בתשלום מוצלח
  const handlePaymentSuccess = async (response: GrowSuccessResponse) => {
    // console.log('Processing successful payment:', response);
    setSuccessMessage("התשלום בוצע בהצלחה! המנוי שלך פעיל.");
    setProcessing(false);

    // מעבר לדף הבית אחרי 2 שניות
    setTimeout(() => {
      navigate("/enduser/dashboard");
    }, 2000);
  };
  const fetchSubscriptionPlans = async () => {
    try {
      const response = await subscriptionService.getSubscriptionPlans();
      if (response.res === "OK") {
        setPlans(response.data || []);
      } else {
        setError(response.msg || "שגיאה בטעינת תוכניות המנוי");
      }
    } catch (err) {
      setError("שגיאה בחיבור לשרת");
    } finally {
      setLoading(false);
    }
  };
  const handleSubscribe = async () => {
    if (!selectedPlan) {
      setError("אנא בחר תוכנית מנוי");
      return;
    }

    if (!saveCardConsent) {
      setError("חובה לאשר את תנאי השימוש והפרטיות");
      return;
    }

    if (!sdkReady) {
      setError("מערכת התשלום עדיין נטענת, נסה שוב");
      return;
    }

    setProcessing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await subscriptionService.createPayment({
        plan_id: selectedPlan,
        save_card_token: 1,
        user_consent: saveCardConsent,
      });
      if (response.res === "OK" && response.data?.auth_code) {
        // console.log('🔑 Got auth_code:', response.data.auth_code);
        // console.log('📋 Full response:', response.data);

        // בדיקה האם SDK קיים
        if (window.growPayment) {
          // console.log('✅ window.growPayment exists');
          // console.log('🚀 Calling renderPaymentOptions...');

          try {
            window.growPayment.renderPaymentOptions(response.data.auth_code);
            // console.log('✅ renderPaymentOptions called successfully');
          } catch (sdkError) {
            console.error("❌ SDK Error:", sdkError);
            setError("שגיאה בפתיחת טופס התשלום");
            setProcessing(false);
          }
        } else {
          console.error("❌ window.growPayment is undefined!");
          setError("מערכת התשלום לא נטענה");
          setProcessing(false);
        }
      } else {
        setError(response.msg || "שגיאה ביצירת תשלום");
        setProcessing(false);
      }
    } catch (err) {
      console.error("Payment error:", err);
      setError("שגיאה בחיבור לשרת");
      setProcessing(false);
    }
  };

  const formatPrice = (price: number) => {
    return subscriptionService.formatPrice(price);
  };

  const calculateSavings = (
    monthlyPrice: number,
    totalPrice: number,
    months: number,
  ) => {
    return subscriptionService.calculateSavings(
      monthlyPrice,
      totalPrice,
      months,
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">טוען תוכניות מנוי...</p>
        </div>
      </div>
    );
  }

  // ===== לוגיקת קופון =====
  // גישת קופון פעילה כרגע?
  const couponActive = subscriptionStatus?.type === "coupon";

  // שעות שנותרו עד תום החלון (לתצוגת badge)
  const couponHoursLeft = (() => {
    if (!couponActive || !subscriptionStatus?.expires_at) return null;
    const ms = new Date(subscriptionStatus.expires_at).getTime() - Date.now();
    if (ms <= 0) return null;
    return Math.ceil(ms / (1000 * 60 * 60));
  })();

  // הצגת מלבן "קוד קופון": רק למשתמש שזכאי דרך 5-ניסיונות (לא מנוי פעיל אחר),
  // וכל עוד אין כבר גישת קופון פעילה. החלטה מעוגנת: משתמש עם מנוי פעיל שאינו
  // 5-ניסיונות אינו רואה את המלבן כלל.
  const showCouponBox =
    !couponActive &&
    (subscriptionStatus?.type === "free_trial" ||
      subscriptionStatus?.type === "no_subscription" ||
      !!subscriptionStatus?.free_trial_activated);

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError("יש להזין קוד קופון");
      return;
    }
    try {
      setCouponSubmitting(true);
      setCouponError(null);
      const result = await subscriptionService.redeemCoupon(couponCode.trim());
      if (result.res === "OK") {
        setShowCouponPopup(false);
        setCouponCode("");
        setSuccessMessage(result.msg || "גישת הקופון הופעלה בהצלחה");
        await verifySession(); // רענון סטטוס → המלבן יתחלף ב-badge
      } else {
        setCouponError(result.msg || "קוד הקופון אינו תקין");
      }
    } catch (err: any) {
      setCouponError("שגיאה במימוש הקופון");
    } finally {
      setCouponSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
      dir="rtl"
    >
      {/* פופאפ הצעת "ניסיון חינם" - מוצג פעם אחת למשתמש זכאי (לוגיקה משותפת עם הדשבורד) */}
      <FreeTrialOfferPopup />

      {/* פופאפ הזנת קוד קופון */}
      {showCouponPopup && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => !couponSubmitting && setShowCouponPopup(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            dir="rtl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">🎟️ הזנת קוד קופון</h3>
              <button
                onClick={() => !couponSubmitting && setShowCouponPopup(false)}
                className="text-gray-400 hover:text-gray-600 text-xl"
                aria-label="סגירה"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              הזן את קוד הקופון שקיבלת כדי לקבל גישה חופשית למחשבונים לזמן מוגבל.
            </p>

            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleRedeemCoupon(); }}
              placeholder="קוד קופון"
              autoFocus
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-center text-lg font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            {couponError && (
              <p className="text-sm text-red-600 mt-3 text-center">{couponError}</p>
            )}

            <div className="flex gap-3 mt-5">
              <button
                onClick={() => !couponSubmitting && setShowCouponPopup(false)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                ביטול
              </button>
              <button
                onClick={handleRedeemCoupon}
                disabled={couponSubmitting}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {couponSubmitting ? "מפעיל..." : "הפעל גישה"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto">
        <div className="text-center">
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            🚗 בחר את תוכנית המנוי שלך
          </h1>
          <p className="mt-4 text-xl text-gray-600">
            גישה מלאה למחשבוני מיסי הרכבים המתקדמים ביותר
          </p>
        </div>

        {/* Badge: גישת קופון פעילה */}
        {couponActive && (
          <div className="mt-6 max-w-md mx-auto">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <p className="text-green-800 font-semibold">
                🎟️ גישת קופון פעילה
                {subscriptionStatus?.coupon_campaign_name
                  ? ` — ${subscriptionStatus.coupon_campaign_name}`
                  : ""}
              </p>
              {couponHoursLeft !== null && (
                <p className="text-sm text-green-700 mt-1">
                  נותרו כ-{couponHoursLeft} שעות גישה חופשית
                </p>
              )}
            </div>
          </div>
        )}

        {/* מלבן "קוד קופון" — רק למשתמש זכאי (5-ניסיונות) */}
        {showCouponBox && (
          <div className="mt-6 max-w-md mx-auto">
            <button
              onClick={() => {
                setCouponError(null);
                setCouponCode("");
                setShowCouponPopup(true);
              }}
              className="w-full bg-white border-2 border-dashed border-blue-300 rounded-xl p-4 text-center hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <span className="text-blue-700 font-semibold">🎟️ יש לך קוד קופון?</span>
              <span className="block text-sm text-gray-500 mt-1">
                לחץ להזנת קוד וקבלת גישה חופשית לזמן מוגבל
              </span>
            </button>
          </div>
        )}

        {error && (
          <div className="mt-8 max-w-md mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-red-400">⚠️</span>
                </div>
                <div className="mr-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        {successMessage && (
          <div className="mt-8 max-w-md mx-auto">
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-green-400">✅</span>
                </div>
                <div className="mr-3">
                  <p className="text-sm font-medium text-green-800">
                    {successMessage}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="mt-12 space-y-4 sm:mt-16 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-6 lg:max-w-4xl lg:mx-auto xl:max-w-none xl:mx-0 xl:grid-cols-3">
          {plans.map((plan) => {
            const savings =
              plan.duration_months > 1
                ? calculateSavings(
                    plans[0]?.monthly_price || 100,
                    plan.total_price,
                    plan.duration_months,
                  )
                : null;

            return (
              <div
                key={plan.id}
                className={`relative bg-white border rounded-lg shadow-sm divide-y divide-gray-200 ${
                  plan.popular
                    ? "border-blue-500 shadow-blue-100"
                    : "border-gray-200"
                } ${selectedPlan === plan.id ? "ring-2 ring-blue-500" : ""}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <span className="inline-flex px-4 py-1 rounded-full text-sm font-semibold bg-blue-500 text-white">
                      🌟 הכי פופולרי
                    </span>
                  </div>
                )}

                <div className="p-6">
                  <h2 className="text-lg leading-6 font-medium text-gray-900">
                    {plan.name_hebrew}
                  </h2>
                  <p className="mt-4 text-sm text-gray-500">
                    {plan.description}
                  </p>
                  <p className="mt-8">
                    <span className="text-4xl font-extrabold text-gray-900">
                      {formatPrice(plan.total_price)}
                    </span>
                    <span className="text-base font-medium text-gray-500">
                      /
                      {plan.duration_months === 0
                        ? "יום"
                        : plan.duration_months === 1
                          ? "חודש"
                          : plan.duration_months === 3
                            ? "רבעון"
                            : "שנה"}
                    </span>
                  </p>



                  {savings && savings.percentage > 0 && (
                    <p className="mt-2 text-sm text-green-600 font-medium">
                      💰 חיסכון של {formatPrice(savings.savings)} (
                      {savings.percentage}%)
                    </p>
                  )}

                  <button
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`mt-8 block w-full rounded-md border px-6 py-3 text-center font-medium ${
                      selectedPlan === plan.id
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-blue-600 border-blue-600 hover:bg-blue-50"
                    }`}
                  >
                    {selectedPlan === plan.id ? "✓ נבחר" : "בחר תוכנית"}
                  </button>
                </div>

                <div className="pt-6 pb-8 px-6">
                  <h3 className="text-xs font-medium text-gray-900 tracking-wide uppercase">
                    מה כלול:
                  </h3>
                  <ul className="mt-6 space-y-4">
                    {plan.features.map((feature, featureIdx) => (
                      <li
                        key={featureIdx}
                        className="flex space-x-3 space-x-reverse"
                      >
                        <span className="text-green-500">✓</span>
                        <span className="text-sm text-gray-500">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* אזור אישור ותשלום */}
        {selectedPlan && (
          <div className="mt-12 max-w-lg mx-auto">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                🔒 אישור תנאי התשלום
              </h3>

              {/* צ'ק בוקס חובה מ-Grow */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="save-card-consent"
                    type="checkbox"
                    checked={saveCardConsent}
                    onChange={(e) => setSaveCardConsent(e.target.checked)}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                </div>
                <div className="mr-3 text-sm">
                  <label
                    htmlFor="save-card-consent"
                    className="font-medium text-gray-700"
                  >
                    <span className="text-red-500">*</span> אני מאשר את{" "}
                    <a
                      href="/terms-and-privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      תנאי השימוש והפרטיות
                    </a>
                  </label>
                  <p className="text-gray-500 text-xs mt-1">
                    המנוי לא יתחדש אוטומטית
                  </p>
                </div>{" "}
              </div>

              <div className="mt-6 border-t pt-4">
                {(() => {
                  const selected = plans.find((p) => p.id === selectedPlan);
                  const isDailyOrMonthly =
                    selected?.plan_name === "daily_plan" ||
                    selected?.plan_name === "monthly_plan";
                  const isQuarterlyOrYearly =
                    selected?.plan_name === "quarterly_plan" ||
                    selected?.plan_name === "yearly_plan";
                  return (
                    <>
                      {isDailyOrMonthly && (
                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                          <span className="text-gray-500">
                            * ללא אפשרות ביטול והחזר
                          </span>
                        </div>
                      )}
                      {isQuarterlyOrYearly && (
                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                          <span className="text-green-600">
                            ✓ ניתן לבטל בכל עת - החזר יחסי
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <button
                onClick={handleSubscribe}
                disabled={!saveCardConsent || processing}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  !saveCardConsent || processing
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                }`}
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                    מעבר לתשלום...
                  </>
                ) : (
                  <>💳 המשך לתשלום מאובטח</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;
